// /home/coder/project/frontend/src/pages/ToCompList.tsx
import React, { useState, useMemo } from 'react'; // Import useMemo
import { useNavigate } from 'react-router-dom';
// highlight-start
import { useGetCompanies, useGetCountries } from '../../../hooks/genericHooks'; // Import both hooks
import { ProtectedRoute, PageGuard } from '../../../components/common/security/ProtectedRoute';
// highlight-end
import { Card, Input, Row, Col, Typography, message, Tag, Select } from 'antd'; // Import Select
import {  SearchOutlined,  PlusOutlined,  BankOutlined as CompanyIcon, } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import StandardTable from '../../../components/common/StandardTable';
import FilterBar from '../../../components/common/FilterBar';
import { createActionColumn } from '../../../components/common/StandardTable';
// highlight-start
import type { Company, CorCountries } from '../../../types'; // Import Company and CorCountries interfaces
import Button from '../../../components/common/Button';
// highlight-end

const { Title } = Typography;

// Interface for filters - Add category, area and relation
interface Filters {
  search: string;
  category: string | null; // Can be null for 'All'
  area: string | null;     // Can be null for 'All'
  country_code: string | null; // Can be null for 'All'
  relation: number | null; // Added relation filter
}
const { Option } = Select; // Add Option from Select

// --- Relation Mapping and Helper ---
const relationOptions = [
  { value: 1, label: 'Root' }, { value: 2, label: 'Partner' }, { value: 3, label: 'Client' },
  { value: 11, label: 'Root Subcon' }, { value: 12, label: 'Root Vendor' },
  { value: 21, label: 'Partner Subcon' }, { value: 22, label: 'Partner Vendor' },
  { value: 33, label: 'Client Subcon' }, { value: 34, label: 'Client Vendor' },
  { value: 35, label: 'Other' },
];

const getRelationLabel = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '-';
  const option = relationOptions.find(opt => opt.value === value);
  return option ? option.label : String(value);
};
// -----------------------------------

// highlight-start
// --- Country Name Helper ---
const getCountryName = (code: string | null | undefined, countries: CorCountries[]): string => {
    if (!code || !countries || countries.length === 0) return '-';
    const country = countries.find(c => c.country_code === code);
    return country ? (country.name ?? code) : code; // Return code if name not found
};
// ---------------------------
// highlight-end

// Helper function to format enum strings
const formatEnumString = (value: string | undefined | null): string => {
  if (!value) return '-';
  const strValue = String(value);
  return strValue.charAt(0).toUpperCase() + strValue.slice(1);
};

// Helper to get color for status tag
const getStatusColor = (status: string | undefined | null): string => {
  const strStatus = String(status).toLowerCase();
  switch (strStatus) {
    case 'active': return 'success';
    case 'inactive': return 'warning';
    case 'terminated': return 'error';
    default: return 'default';
  }
};

const ToCompList: React.FC = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<Filters>({
    search: '',
    category: null, // Initialize new filters
    area: null,
    country_code: null,
    relation: null, // Added relation filter
  });

  // --- Data Fetching ---
  // highlight-start
  const { data: companiesData, isLoading: isLoadingCompanies, isError: isErrorCompanies, error: errorCompanies } = useGetCompanies();
  const companyList = companiesData?.data || [];

  const { data: countriesData, isLoading: isLoadingCountries, isError: isErrorCountries, error: errorCountries } = useGetCountries();
  const countries = countriesData?.data || [];

  const isLoading = isLoadingCompanies || isLoadingCountries;
  const isError = isErrorCompanies || isErrorCountries;
  const error = errorCompanies || errorCountries; // Combine errors (show first one)
  // highlight-end

  // Handle error state
  if (isError) {
    // highlight-start
    let combinedErrorMessage = 'Could not load data: ';
    if (errorCompanies) combinedErrorMessage += `Companies (${errorCompanies.message}) `;
    if (errorCountries) combinedErrorMessage += `Countries (${errorCountries.message}) `;
    console.error("Failed to fetch data:", error);
    message.error(combinedErrorMessage.trim());
    // highlight-end
  }

  // --- Derive Unique Filter Options ---
  const uniqueCategories = useMemo(() => {
    if (!companyList) return [];
    const categories = companyList.map(c => c.category).filter((cat): cat is string => !!cat);
    return Array.from(new Set(categories));
  }, [companyList]);

  const uniqueAreas = useMemo(() => {
    if (!companyList) return [];
    const areas = companyList.map(c => c.area).filter((area): area is string => !!area);
    return Array.from(new Set(areas));
  }, [companyList]);
  // ------------------------------------

  // Filter companies based on the search term
  const filteredCompanies = companyList.filter((company) => {
    if (filters.search && filters.search.trim() !== '') {
      const searchTerm = filters.search.toLowerCase();
      const searchFields = [
        company.short_code?.toString() || '',
        company.name?.toLowerCase() || '',
        //getRelationLabel(company.relation).toLowerCase() || '',
        company.address?.toLowerCase() || '',
        company.phone?.toLowerCase() || '',
        company.email?.toLowerCase() || '',
        company.status?.toLowerCase() || '',
      ];

      if (!searchFields.some(field => field.includes(searchTerm))) {
        return false;
      }
    }
    // Filter by category
    if (filters.category && company.category !== filters.category) {
      return false;
    }
    // Filter by area
    if (filters.area && company.area !== filters.area) {
      return false;
    }
    // Filter by country
    if (filters.country_code && company.country_code !== filters.country_code) {
      return false;
    }
    // Filter by relation
    if (filters.relation !== null && company.relation !== filters.relation) {
      return false;
    }

    return true;
  });

  // Define table columns (use any[] to allow exportValue on computed columns)
  const columns: any[] = [
    {
      title: 'Code',
      dataIndex: 'short_code',
      key: 'short_code',
      sorter: (a: Company, b: Company) => (a.short_code || '').localeCompare(b.short_code || ''),
      width: 90,
      render: (code: any) => code || '-',
      exportValue: (r: Company) => r.short_code || '-',
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: Company, b: Company) => (a.name || '').localeCompare(b.name || ''),
      width: 200,
      ellipsis: true,
      exportValue: (r: Company) => r.name || '-',
    },
    {
      title: 'Relation',
      dataIndex: 'relation',
      key: 'relation',
      render: (relation: any) => getRelationLabel(relation),
      sorter: (a: Company, b: Company) => getRelationLabel(a.relation).localeCompare(getRelationLabel(b.relation)),
      width: 120,
      ellipsis: true,
      exportValue: (r: Company) => getRelationLabel(r.relation),
    },
    // highlight-start
    {
      title: 'Country',
      dataIndex: 'country_code',
      key: 'country',
      render: (code: any) => getCountryName(code, countries), // Use helper to display name
      sorter: (a: Company, b: Company) => getCountryName(a.country_code, countries).localeCompare(getCountryName(b.country_code, countries)), // Sort by name
      width: 130, // Adjust width
      ellipsis: true,
      exportValue: (r: Company) => getCountryName(r.country_code as any, countries),
    },
    // highlight-end
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      render: (phone: any) => phone || '-',
      width: 120,
      exportValue: (r: Company) => r.phone || '-',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (email: any) => email || '-',
      width: 180,
      ellipsis: true,
      exportValue: (r: Company) => r.email || '-',
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (category: any) => formatEnumString(category),
      sorter: (a: Company, b: Company) => (a.category || '').localeCompare(b.category || ''),
      width: 100,
      ellipsis: true,
      exportValue: (r: Company) => formatEnumString(r.category as any),
    },
    {
      title: 'Area',
      dataIndex: 'area',
      key: 'area',
      render: (area: any) => formatEnumString(area),
      sorter: (a: Company, b: Company) => (a.area || '').localeCompare(b.area || ''),
      width: 120,
      ellipsis: true,
      exportValue: (r: Company) => formatEnumString(r.area as any),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: any) => (
        <Tag color={getStatusColor(status)}>
          {formatEnumString(status)}
        </Tag>
      ),
      sorter: (a: Company, b: Company) => (a.status || '').localeCompare(b.status || ''),
      width: 90,
      exportValue: (r: Company) => formatEnumString(r.status as any),
    },
  ];

  return (
      <PageGuard>
        <Card>
          <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
            <Col>
              <Title level={4} style={{ margin: 0 }}>
                <CompanyIcon style={{ marginRight: 8 }} />
                Company List
              </Title>
            </Col>
            <Col>
              <Button action="add" type="primary" icon={<PlusOutlined />} onClick={() => navigate('/to/companies/create')}>
                Add Company
              </Button>
            </Col>
          </Row>

          <FilterBar
            items={[
              { type: 'text', key: 'q', placeholder: 'Search...', value: filters.search, onChange: (v:string) => setFilters(f => ({ ...f, search: v })), width: 200 },
              { type: 'select', key: 'category', placeholder: 'Category', value: filters.category as any, onChange: (v:any) => setFilters(f => ({ ...f, category: v || null })), options: uniqueCategories.map(cat => ({ value: cat, label: formatEnumString(cat) })), width: 140, allowClear: true },
              { type: 'select', key: 'area', placeholder: 'Area', value: filters.area as any, onChange: (v:any) => setFilters(f => ({ ...f, area: v || null })), options: uniqueAreas.map(area => ({ value: area, label: formatEnumString(area) })), width: 140, allowClear: true },
              { type: 'select', key: 'country', placeholder: 'Country', value: filters.country_code as any, onChange: (v:any) => setFilters(f => ({ ...f, country_code: v || null })), options: countries.map(c => ({ value: c.country_code, label: c.name ?? c.country_code })), width: 160, showSearch: true, allowClear: true },
              { type: 'select', key: 'relation', placeholder: 'Relation', value: filters.relation as any, onChange: (v:any) => setFilters(f => ({ ...f, relation: v ?? null })), options: relationOptions.map(o => ({ value: o.value, label: o.label })), width: 150, allowClear: true },
            ]}
          />

          <StandardTable
            dataSource={filteredCompanies}
            columns={[
              ...columns,
              createActionColumn<Company>({
                title: 'Actions',
                onView: (rec) => navigate(`/to/companies/show/${rec.id}`),
                onEdit: (rec) => navigate(`/to/companies/edit/${rec.id}`),
                showDelete: false,
              })
            ] as any}
            rowKey="id"
            loading={isLoading}
            bordered
            size="small"
            scroll={{ x: 'max-content' }}
            sticky
          />
        </Card>
      </PageGuard>
  );
};

export default ToCompList;
